import { webPageSchema } from "@/lib/schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Photo } from "@/components/site/Photo";
import { GALERIA_FOTOS, GALERIA_GRUPOS, GALERIA_SIZES } from "@/lib/galeria";
import { MAIN_PHONE, waLink } from "@/lib/whatsapp";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import { ogImageMeta } from "@/lib/og-image";
import { PageHero } from "@/components/site/PageHero";
import { PanelSection } from "@/components/site/PanelSection";
import { PageCta } from "@/components/site/PageCta";
import { AccesosRapidos } from "@/components/site/AccesosRapidos";

const TITLE = "Galería | Residencia Oasis Cuernavaca";
const DESCRIPTION =
  "Fotos reales de Residencia Oasis Cuernavaca: habitaciones individuales, comedor, jardines y la vida diaria de los residentes. Vea la casa antes de visitarla.";
const URL = absoluteUrl("/galeria-de-fotos");

export const Route = createFileRoute("/galeria-de-fotos")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      ...ogImageMeta(),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          webPageSchema({ name: "Galería de Residencia Oasis Cuernavaca", description: DESCRIPTION, path: "/galeria-de-fotos", type: "CollectionPage" }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          name: "Galería de Residencia Oasis Cuernavaca",
          description: DESCRIPTION,
          url: URL,
          image: GALERIA_FOTOS.map((f) => ({
            "@type": "ImageObject",
            contentUrl: `${SITE_URL}${f.webp}`,
            name: f.titulo ?? f.alt,
            description: f.alt,
            caption: f.caption,
            width: f.width,
            height: f.height,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Galería", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  const wa = waLink("galería", "quiero agendar una visita para ver la casa");

  return (
    <>
      <PageHero
        breadcrumb="Galería"
        title="Galería de Residencia Oasis Cuernavaca"
        answer="Fotos reales de la casa: habitaciones, comedor, jardines y la vida diaria."
      />

      <PanelSection tone="light" label="Fotos de la residencia">
        {GALERIA_GRUPOS.map((grupo) => {
          const fotos = GALERIA_FOTOS.filter((f) => f.grupo === grupo);
          if (fotos.length === 0) return null;
          return (
            <div key={grupo} className="mt-8 first:mt-0">
              <h2 className="h-ask">{grupo}</h2>
              <ul className="gallery-grid mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {fotos.map((f, i) => (
                  <li key={f.id}>
                    <figure>
                      <Photo
                        avif={f.avif}
                        webp={f.webp}
                        avifSrcSet={f.avifSrcSet}
                        webpSrcSet={f.webpSrcSet}
                        width={f.width}
                        height={f.height}
                        sizes={GALERIA_SIZES}
                        alt={f.alt}
                        title={f.titulo}
                        priority={grupo === GALERIA_GRUPOS[0] && i === 0}
                        className="photo-plate aspect-[4/3] w-full object-cover"
                      />
                      <figcaption className="mt-2 text-[0.95rem] text-muted-foreground">
                        {f.caption}
                      </figcaption>
                    </figure>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        <p className="mt-8 text-muted-foreground">
          ¿Quiere ver los cuartos con calma?{" "}
          <Link to="/contacto" className="underline">
            Agende una visita guiada
          </Link>{" "}
          de lunes a domingo, de 8:00 a 18:00 h.
        </p>
      </PanelSection>

      <AccesosRapidos variant="panel" exclude={["/galeria-de-fotos"]} />

      <PageCta
        waHref={wa}
        waLabel="Agenda una visita (lunes a domingo, 8:00-18:00 h)"
      />
    </>
  );
}
